import {
  CloudUploadSharp,
  DeleteSharp,
  InsertDriveFileSharp,
} from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  InputLabel,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { useSnackbar } from "../contexts/SnackbarContext";
import { uploadImage, viewImage } from "../services/upload.service";

type PropsType = {
  files: string[];
  setFiles: (files: string[]) => void;
  readOnly?: boolean;
};

const Upload = (props: PropsType) => {
  const { files, setFiles, readOnly = false } = props;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  const [isLoading, setIsLoading] = useState(false);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (files.length >= 5) {
      showSnackbar(`Upload Up to 5 Files`, "warning");
      return;
    } else {
      const file = e.target.files?.[0];
      if (file) {
        await uploadFile(file);
      }
    }
  };

  const uploadFile = async (file: File): Promise<object> => {
    const fileExtension = file.name.split(".").pop();

    const formData = new FormData();
    formData.append("file", file);
    formData.append("file-name", `${uuidv4()}.${fileExtension}`);

    setIsLoading(true);
    try {
      const response = await uploadImage(formData);
      if (response) {
        let items = files;
        items.push(response?.fileName);
        setFiles(items);
        const fileUrl = viewImage(response?.fileName);
        if (fileUrl) {
          return {
            fileName: response?.fileName,
            fileUrl: fileUrl,
          };
        }
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      showSnackbar("Invalid File", "error");
    } finally {
      setIsLoading(false);
    }

    return {};
  };

  const onDelete = (index: number) => () => {
    const updatedFiles = [...files];
    updatedFiles.splice(index, 1);
    setFiles(updatedFiles);
  };

  return (
    <>
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
        useFlexGap
        gap={2}
      >
        <InputLabel htmlFor="capture">Attach More Files</InputLabel>
        {!readOnly && files && files?.length > 0 && (
          <Button
            type="button"
            variant="contained"
            className="w-full md:max-w-[120px]"
            startIcon={<CloudUploadSharp className="text-white" />}
            onClick={() => fileInputRef.current?.click()}
          >
            Upload
          </Button>
        )}
      </Stack>
      <Box
        style={{
          minHeight: "140px",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: theme.vars.palette.grey[100],
          border: `1px dashed ${theme.vars.palette.grey[300]}`,
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          alignItems: "center",
          justifyContent: "center",
          overflowY: "auto",
          maxHeight: "170px",
        }}
      >
        {isLoading ? (
          <>...Loading...</>
        ) : (
          <>
            {files && files?.length > 0 ? (
              <Stack direction={"column"} useFlexGap gap={1} width={"100%"}>
                {files.map((file, index) => (
                  <Stack
                    direction={"row"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                    key={`file-${index}`}
                  >
                    <Stack
                      direction={"row"}
                      justifyContent="center"
                      alignItems="center"
                      useFlexGap
                      gap={2}
                    >
                      <InsertDriveFileSharp className="w-6 h-6" />
                      <Typography variant="body2">
                        <a
                          href={viewImage(file)}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          {file}
                        </a>
                      </Typography>
                    </Stack>
                    {!readOnly && (
                      <IconButton color="error" onClick={onDelete(index)}>
                        <DeleteSharp className="w-6 h-6" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            ) : (
              <>
                {!readOnly && (
                  <>
                    <Stack
                      direction={"column"}
                      useFlexGap
                      justifyContent={"center"}
                      alignItems={"center"}
                      gap={1}
                    >
                      <Typography>Click to upload files here</Typography>
                      <Typography color="slate.500" variant="body2">
                        Upload up to 5 files
                      </Typography>
                    </Stack>
                    <Button
                      type="button"
                      variant="contained"
                      className="w-full md:max-w-[120px]"
                      startIcon={<CloudUploadSharp className="text-white" />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload
                    </Button>
                  </>
                )}
              </>
            )}
          </>
        )}

        <input
          ref={fileInputRef}
          type="file"
          style={{ display: "none" }} // Hide the file input
          onChange={handleImageChange}
        />
      </Box>
      <Stack
        direction={"row"}
        useFlexGap
        gap={2}
        justifyContent={"space-between"}
      >
        <Typography variant="body2">
          Uploaded: {files?.length ?? 0} / 5
        </Typography>
        <Typography variant="body2" color="grey.400">
          File size must not exceed 4MB.
        </Typography>
      </Stack>
    </>
  );
};

export default Upload;
